<template>
  <input
    ref="inputRef"
    type="tel"
    class="form-control"
    :class="size ? `form-control-${size}` : null"
    :placeholder="placeholder"
    :disabled="disabled"
    @focus="pinCaretToEnd"
    @click="pinCaretToEnd"
    @mouseup="pinCaretToEnd"
    @keyup="pinCaretToEnd"
    @keydown="onKeydown"
  />
</template>

<script>
import { useCurrencyInput } from 'vue-currency-input'

export default {
  name: 'CurrencyBaseInput',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      type: [Number, String],
      default: 0,
    },

    modelValue: {
      type: [Number, String],
      default: null,
    },

    placeholder: {
      type: String,
      default: '',
    },

    isPercentage: {
      type: Boolean,
      default: false,
    },

    options: {
      type: Object,
      default() {
        return {}
      },
    },

    isDiscount: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    allowEmpty: {
      type: Boolean,
      default: false,
    },

    size: {
      type: String,
      default: '',
    },

    max: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      applyingCap: false,
    }
  },

  computed: {
    currentValue() {
      const raw = this.modelValue != null ? this.modelValue : this.value
      if (this.allowEmpty && (raw === '' || raw == null)) return null
      const next = Number(raw)
      return Number.isNaN(next) ? 0 : next
    },
  },

  setup(props) {
    const { inputRef, setValue, numberValue } = useCurrencyInput(
      props.options,
      false
    )

    return { inputRef, setValue, numberValue }
  },

  watch: {
    currentValue(value) {
      const current = this.numberValue
      if (value == null) {
        if (current == null) return
      } else if (current != null && Number(current) === Number(value)) {
        return
      }
      this.$nextTick(() => this.setValue(value))
    },

    max() {
      const capped = this.applyCap(this.numberValue)
      if (capped == null || this.currentValue === capped) return
      this.$emit('input', capped)
      this.$emit('change', capped)
    },

    numberValue(value) {
      if (this.applyingCap) return
      if (value == null || Number.isNaN(Number(value))) return

      let parsed = Math.round(Number(value) * 100) / 100

      if (this.isPercentage) {
        parsed = Math.min(100, Math.max(0, parsed))
      }

      const capped = this.applyCap(parsed)
      if (capped !== parsed) parsed = capped

      if (this.currentValue === parsed) return
      this.$emit('input', parsed)
      this.$emit('change', parsed)
    },
  },

  mounted() {
    this.$nextTick(() => this.setValue(this.currentValue))
  },

  methods: {
    applyCap(value) {
      if (this.max == null || value == null || Number.isNaN(Number(value))) {
        return value
      }
      const cap = Math.round(Math.max(Number(this.max), 0) * 100) / 100
      const current = Math.round(Number(value) * 100) / 100
      if (current <= cap) return current
      this.applyingCap = true
      this.setValue(cap)
      this.applyingCap = false
      return cap
    },

    nativeInput() {
      const input = this.inputRef || this.$el
      if (!input) return null
      if (input.tagName === 'INPUT') return input
      if (input.$el && input.$el.tagName === 'INPUT') return input.$el
      return null
    },

    moveCaretToEnd() {
      const input = this.nativeInput()
      if (!input || typeof input.setSelectionRange !== 'function') return
      const end = (input.value || '').length
      input.setSelectionRange(end, end)
    },

    pinCaretToEnd() {
      this.moveCaretToEnd()
      this.$nextTick(() => {
        this.moveCaretToEnd()
        setTimeout(() => this.moveCaretToEnd(), 0)
      })
    },

    onKeydown(event) {
      if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.key === 'Home'
      ) {
        event.preventDefault()
        this.moveCaretToEnd()
      }
    },

    focus() {
      const input = this.nativeInput()
      if (input && typeof input.focus === 'function') {
        input.focus()
      }
    },
  },
}
</script>
